import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState, useEffect, useRef } from 'react';
import SolutionGroup from './SolutionGroup/SolutionGroup';
import Filter from './Filter/Filter';
const DocumentApp = ({ pageData }) => {
    const [selectedSolutions, setSelectedSolutions] = useState([]), [order, setOrder] = useState(null), hasScrolledRef = useRef(false);
    // handle filter changes
    const handleFilterChange = (groupLabel, value, checked) => {
        if (groupLabel === 'Solutions') {
            if (checked) {
                setSelectedSolutions(prev => [...prev, value]);
            }
            else {
                setSelectedSolutions(prev => prev.filter(slug => slug !== value));
            }
        }
        else if (groupLabel === 'Order by') {
            if (checked) {
                setOrder(value);
            }
            else {
                setOrder(null);
            }
        }
    };
    // filtered list of documents
    const filteredDocuments = useMemo(() => {
        if (!pageData.length)
            return [];
        let filtered = pageData
            .filter(solution => {
            // filter by selected solution
            if (selectedSolutions.length > 0) {
                return selectedSolutions.includes(solution.slug);
            }
            return true;
        })
            .filter(solution => solution.documents.length > 0);
        // Apply sorting
        if (order !== null) {
            filtered = filtered
                .map(solution => ({
                ...solution,
                documents: [...solution.documents].sort((a, b) => {
                    const comparison = a.title.localeCompare(b.title);
                    return order === 'asc' ? comparison : -comparison;
                })
            }))
                .sort((a, b) => {
                const comparison = a.name.localeCompare(b.name);
                return order === 'asc' ? comparison : -comparison;
            });
        }
        return filtered;
    }, [pageData, selectedSolutions, order]);
    // filters
    const orderGroup = {
        label: 'Order by',
        selectMultiple: false,
        filters: [
            {
                label: 'Alphabetical: A to Z',
                value: 'asc',
                checked: order === 'asc'
            },
            {
                label: 'Alphabetical: Z to A',
                value: 'desc',
                checked: order === 'desc'
            }
        ]
    };
    const instanceFilters = useMemo(() => {
        if (!pageData.length)
            return [];
        if (pageData.length === 1)
            return [orderGroup];
        const solutionFilters = pageData
            .map(solution => ({
            label: solution.name,
            value: solution.slug,
            checked: selectedSolutions.includes(solution.slug)
        }));
        const solutionGroup = {
            label: 'Solutions',
            selectMultiple: true,
            filters: solutionFilters
        };
        return [orderGroup, solutionGroup];
    }, [pageData, selectedSolutions, order]);
    useEffect(() => {
        // Check if there's a hash in the url
        const hash = window.location.hash.slice(1);
        if (hash && !hasScrolledRef.current) {
            const scrollToHash = () => {
                const el = document.getElementById(hash);
                if (el) {
                    el.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                    // set scroll margin for fixed headers
                    el.style.scrollMarginTop = '84px';
                    hasScrolledRef.current = true;
                    return true;
                }
                return false;
            };
            // Try immediately
            if (!scrollToHash()) {
                // If not found, retry a few times with increasing delays
                let attempts = 0;
                const maxAttempts = 3;
                const retryInterval = setInterval(() => {
                    attempts++;
                    if (scrollToHash() || attempts >= maxAttempts) {
                        clearInterval(retryInterval);
                    }
                }, 100);
            }
            else {
                hasScrolledRef.current = true;
            }
        }
    }, []);
    return (_jsxs(_Fragment, { children: [_jsx("aside", { className: "border-b border-neutral-200", children: _jsxs("div", { className: "container grid md:grid-cols-4 gap-0 items-stretch", children: [_jsx("div", { className: "py-4 pr-12 border-r border-neutral-200 hidden md:flex items-center", children: _jsx("h3", { className: "text-base font-medium text-neutral-500 font-sans mb-0", children: "Solution / Benefit" }) }), _jsxs("div", { className: "col-span-3 py-4 md:pl-12 flex justify-between items-center", children: [_jsx("h3", { className: "text-base font-medium text-neutral-500 font-sans mb-0", children: "Documents & Files" }), _jsx(Filter, { filterGroups: instanceFilters, onFilterChange: handleFilterChange })] })] }) }), filteredDocuments.map((solution) => (_jsx(SolutionGroup, { term_id: solution.term_id, name: solution.name, slug: solution.slug, documents: solution.documents }, solution.term_id)))] }));
};
export default DocumentApp;
